/**
 * Vendored copy of Payload's Lexical UploadServerNode.
 *
 * Payload's `upload` node is a `DecoratorBlockNode` carrying a `__data` blob
 * ({ id, fields, pending, relationTo, value }). Vanilla Lexical has no upload
 * node, so round-tripping a Payload `content` tree without this class drops the
 * media reference entirely. This reproduces Payload's serialization contract so
 * `serialize(parse(tree))` deep-equals `tree`.
 *
 * Source of truth (do not drift):
 *   @payloadcms/richtext-lexical/dist/features/upload/server/nodes/UploadNode.js
 *
 * Difference from Payload source: `decorate()` returns a minimal read-only
 * preview built only from `__data` — no Payload `RawUploadComponent`, no
 * network fetch. That heavy client component pulls in the whole Payload admin
 * runtime; the inline editor only needs to render a placeholder for the media.
 * `importDOM`/`exportDOM`/`$convertUploadElement` are omitted (no HTML paste
 * path in the round-trip + editing flow).
 */
import { DecoratorBlockNode } from "@lexical/react/LexicalDecoratorBlockNode"
import { addClassNamesToElement } from "@lexical/utils"
import ObjectID from "bson-objectid"
import {
  $applyNodeReplacement,
  type DOMConversionMap,
  type EditorConfig,
  type ElementFormatType,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
} from "lexical"
import { useEffect, useState, type JSX } from "react"
import { CMS_BASE_URL, cmsMediaUrl } from "@/lib/cms"

/** Pending-upload state Payload sets while a freshly-dropped file uploads. */
export type UploadPending = {
  formID?: string
  src?: string
  [key: string]: unknown
}

/**
 * The `__data` blob on an upload node. `value` is the media doc id (number for
 * an integer PK, string for an ObjectID PK); `fields` holds any configured
 * extra upload fields. `pending` is only present mid-upload in the live editor
 * and is absent from saved content.
 */
export type UploadData = {
  fields: Record<string, unknown>
  id?: string
  pending?: UploadPending
  relationTo: string
  value: number | string
}

export type SerializedUploadNode = Spread<
  Spread<
    {
      type: "upload"
      version: 3
    },
    UploadData
  >,
  SerializedLexicalNode & { format: ElementFormatType }
>

/**
 * The shape `importJSON` actually receives off the wire: older Payload exports
 * carry `version` 1/2 and trigger the migration shims, so the version field is
 * widened from the literal-3 export type.
 */
type IncomingUploadNode = Omit<SerializedUploadNode, "version"> & {
  version: number
}

export class UploadNode extends DecoratorBlockNode {
  __data: UploadData

  constructor({
    data,
    format,
    key,
  }: {
    data: UploadData
    format?: ElementFormatType
    key?: NodeKey
  }) {
    super(format, key)
    this.__data = data
  }

  static override clone(node: UploadNode): UploadNode {
    return new this({
      data: node.__data,
      format: node.__format,
      key: node.__key,
    })
  }

  static override getType(): string {
    return "upload"
  }

  static override importDOM(): DOMConversionMap | null {
    return null
  }

  static override importJSON(serializedNode: IncomingUploadNode): UploadNode {
    if (
      serializedNode.version === 1 &&
      (serializedNode.value as unknown as { id?: unknown } | null)?.id
    ) {
      serializedNode.value = (
        serializedNode.value as unknown as { id: number | string }
      ).id
    }
    if (serializedNode.version === 2 && !serializedNode.id) {
      serializedNode.id = new ObjectID().toHexString()
      serializedNode.version = 3
    }
    const importedData: UploadData = {
      id: serializedNode.id,
      fields: serializedNode.fields,
      pending: serializedNode.pending,
      relationTo: serializedNode.relationTo,
      value: serializedNode.value,
    }
    const node = $createUploadNode({ data: importedData })
    node.setFormat(serializedNode.format)
    return node
  }

  static isInline(): false {
    return false
  }

  override createDOM(config?: EditorConfig): HTMLElement {
    const element = document.createElement("div")
    addClassNamesToElement(element, config?.theme?.upload)
    return element
  }

  override decorate(): JSX.Element {
    // Read-only image preview. A freshly-dropped file carries a local
    // `pending.src` (blob URL) we can show immediately; a saved node carries only
    // the media id, so `UploadPreview` resolves the media doc's `url`
    // client-side. Falls back to a labeled placeholder if it can't resolve. This
    // is render-only — it never touches serialization, so the round-trip stays
    // exact. (AHD-7515 / finding 7)
    const data = this.__data
    return (
      <UploadPreview
        relationTo={data.relationTo}
        value={String(data.value)}
        pendingSrc={data.pending?.src}
      />
    )
  }

  override exportJSON(): SerializedUploadNode {
    return {
      ...super.exportJSON(),
      ...this.getData(),
      type: "upload",
      version: 3,
    }
  }

  getData(): UploadData {
    return this.getLatest().__data
  }

  setData(data: UploadData): void {
    const writable = this.getWritable()
    writable.__data = data
  }

  override updateDOM(): false {
    return false
  }
}

/**
 * Read-only image preview for an upload node, shown inside the editor only.
 * Resolves the media doc's `url` from `<CMS base>/<relationTo>/<id>` once, then
 * renders an image element. Until it resolves (and if it can't) it shows a clearly
 * labeled placeholder block carrying the media reference — never the raw
 * `media:media/NNN` text. The classes are scoped under `.ahead-lexical-editor`
 * in `lexical-editor.css`.
 */
function UploadPreview({
  relationTo,
  value,
  pendingSrc,
}: {
  relationTo: string
  value: string
  pendingSrc?: string
}) {
  // A pending (just-dropped) upload has a local blob src we can render at once.
  const [src, setSrc] = useState<string | null>(pendingSrc ?? null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (pendingSrc) return
    if (!CMS_BASE_URL) {
      setFailed(true)
      return
    }
    let cancelled = false
    fetch(`${CMS_BASE_URL}/api/${relationTo}/${value}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status))
        const doc = (await res.json()) as { url?: string }
        if (cancelled) return
        const resolved = cmsMediaUrl(doc.url)
        if (resolved) setSrc(resolved)
        else setFailed(true)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [relationTo, value, pendingSrc])

  if (src && !failed) {
    return (
      <span
        className="ahead-lexical-upload"
        data-lexical-upload-id={value}
        data-lexical-upload-relation-to={relationTo}
      >
        {/* eslint-disable-next-line @next/next/no-img-element -- editor-only preview chunk (never ships to public); src is an arbitrary CMS media URL, not a next/image-optimizable asset */}
        <img src={src} alt="" />
      </span>
    )
  }

  return (
    <span
      className="ahead-lexical-upload"
      data-lexical-upload-id={value}
      data-lexical-upload-relation-to={relationTo}
    >
      <span className="ahead-lexical-upload-fallback">
        🖼 Image · {relationTo} #{value}
        {failed ? " (preview unavailable)" : " (loading…)"}
      </span>
    </span>
  )
}

export function $createUploadNode({ data }: { data: UploadData }): UploadNode {
  if (!data?.id) {
    data.id = new ObjectID().toHexString()
  }
  return $applyNodeReplacement(new UploadNode({ data }))
}

export function $isUploadNode(
  node: LexicalNode | null | undefined
): node is UploadNode {
  return node instanceof UploadNode
}
